import { useQuery, UseQueryOptions } from "@tanstack/react-query"
import { Skill } from "../modules/preferences/types/preferences.types"
import { ENRICHED_PROFILE } from "../shared/constants/QUERY_KEYS";
import { GET_PROFILE } from "../shared/constants/URLS"
import { httpLegacyClient } from "../shared/http-client"

interface Photo {
  path: string
  location: string
  source: string
  url: string
  uploaded_at: Date
  scan_status: string
}

interface Area {
  id: string
  name: string
  lat: number
  long: number
}

interface City {
  id: string
  name: string
  photo_firebase_path: string
}

interface EducationLevel {
  id: string
  level: string
  ordered_id: number
}

interface Course {
  id: string
  title: string
  op_verification_status: string
  source: string
  verification_source: string
}

interface EducationInstitute {
  id: string
  title: string
}

interface Specialization {
  id: string
  title: string
  verification_status: string
  source: string
  verification_source: string
}

interface Education {
  id: string
  education_level: EducationLevel
  course: Course
  specialization: Specialization
  education_institute: EducationInstitute
  education_type: string
  start: Date
  end: Date
  ongoing: boolean
  created_at: Date
  updated_at: Date
}

interface HighestEducationLevel {
  id: string
  level: string
  ordered_id: number
}

interface Headline {
  experience_headline: string
  education_headline: string
}

interface Docs {}

export interface Organization {
  id: string
  title: string
  standard_organisation_id: string;
  gumlet_url?: string;
}

export interface Industry {
  id: string
  title: string
}

export interface Department {
  id: string
  title: string
}

export interface SubDepartment {
  id: string
  title: string
}

export interface JobTitle {
  id: string
  title: string
  classification: "low_skill" | ""
}

export interface Experience {
    id: string;
    organization: Organization;
    industry: Industry;
    skills: Skill[];
    department: Department;
    sub_department: SubDepartment;
    employment_type: string;
    salary: number;
    job_title: JobTitle;
    start: string;
    end: string;
    ongoing: boolean;
}

type LanguageSkillProficiencyType = {
  name: string
  proficiency: number
}

export interface LanguagesKnown {
  created_at: string;
  id:string;
  language_id: string
  language_name: string
  language_skills: LanguageSkillProficiencyType[] | [];
}

export interface Profile {
    user_id: string;
    created_at: Date;
    updated_at: Date;
    full_name: string;
    gender: string;
    slug: string;
    profile_url: string;
    photo: Photo;
    area: Area;
    city: City;
    is_verified: boolean;
    apna_vip: boolean;
    educations: Education[];
    experiences?: Experience[];
    known_languages?: any;
    skills: Skill[];
    resume?: { format: string; page_count: number; url: string };
    years_of_experience: number;
    months_of_experience: number;
    is_experienced: boolean;
    highest_education_level: HighestEducationLevel;
    headline: Headline;
    hometown?: any;
    industry_experiences: any[];
    visual_prefs?: any;
    age: number;
    current_salary: number;
    notice_period: number;
    current_salary_v2: number;
    date_of_birth?: any;
    docs: Docs;
    assets: any[];
    audio_intro?: any;
    contact_info?: any;
    non_available_assets: null | string[]; 
    non_available_docs: null | string[];
    docs_v2:any[];
}

export interface ParsedResumeProfile {
  is_resume_parsed: boolean;
  profile?: Profile | null;
}


const useGetProfile = (options: UseQueryOptions<Profile> = {}) => {
  return useQuery<Profile>(
      [ENRICHED_PROFILE],
      () => {
          return httpLegacyClient.get<Profile>(GET_PROFILE).then((resp) => resp.data);
      },
      { ...options }
  );
}

export default useGetProfile
